import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [impersonationContext, setImpersonationContext] = useState(null);

  // Axios interceptor for auth token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Response interceptor to handle 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('401 error, logging out');
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check setup status and load user
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First check if system needs setup
        const setupResponse = await axios.get(`${API_BASE}/api/setup/check`);
        if (setupResponse.data.needs_setup) {
          setNeedsSetup(true);
          setLoading(false);
          return;
        }

        // Check if we have a token and validate it
        const token = localStorage.getItem('token');
        if (token) {
          console.log('Token found, setting authorization header');
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          try {
            // Test the token by getting current user info
            const userResponse = await axios.get(`${API_BASE}/api/auth/me`);
            setUser(userResponse.data);
            console.log('User authenticated successfully:', userResponse.data.email);
            console.log('User role:', userResponse.data.role);
            console.log('Is impersonating:', userResponse.data.is_impersonating);
            
            // Set impersonation context from user data
            if (userResponse.data.is_impersonating) {
              console.log('Setting impersonation context for:', userResponse.data.impersonating_org_name);
              setImpersonationContext({
                orgId: userResponse.data.organization_id,
                orgName: userResponse.data.impersonating_org_name,
                originalRole: userResponse.data.original_role
              });
            } else {
              setImpersonationContext(null);
            }
          } catch (error) {
            console.log('Token validation failed:', error.message);
            // Token is invalid, remove it
            localStorage.removeItem('token');
            localStorage.removeItem('token_expiration');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Helper function to parse JWT token
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null;
    }
  };

  // Check if token is expired (with better debugging)
  const isTokenExpired = () => {
    const expiration = localStorage.getItem('token_expiration');
    if (!expiration) {
      console.log('No token expiration found');
      return true;
    }
    
    const expirationTime = parseInt(expiration, 10);
    const currentTime = Date.now();
    const isExpired = currentTime > expirationTime;
    
    if (isExpired) {
      console.log('Token expired:', new Date(expirationTime), 'Current:', new Date(currentTime));
    }
    
    return isExpired;
  };

  const login = async (email, password, antiBotData = {}) => {
    try {
      // Normalize email: lowercase and trim whitespace to prevent case-sensitivity issues
      const normalizedEmail = email.toLowerCase().trim();
      
      const response = await axios.post(`${API_BASE}/api/auth/login`, {
        email: normalizedEmail,
        password,
        ...antiBotData
      });
      
      // MULTI-CLUB: if user belongs to multiple clubs, backend returns memberships + temp_token
      if (response.data.multi_org) {
        // Check if a preferred organization was previously selected (remembered choice)
        const preferredOrgId = localStorage.getItem(`preferred_org:${normalizedEmail}`);
        const memberships = response.data.memberships || [];
        const preferredMembership = preferredOrgId
          ? memberships.find(m => m.organization_id === preferredOrgId)
          : null;
        
        if (preferredMembership) {
          // Auto-select previously chosen club (silent multi-org login)
          return await selectOrganization(response.data.temp_token, preferredMembership.organization_id);
        }
        
        // Need user to pick a club - return memberships info to caller (Login page)
        return {
          success: true,
          multi_org: true,
          temp_token: response.data.temp_token,
          memberships,
          full_name: response.data.full_name,
          email: normalizedEmail,
        };
      }
      
      const { access_token, user: userData } = response.data;
      
      // Parse token to get expiration time
      const tokenPayload = parseJwt(access_token);
      if (tokenPayload && tokenPayload.exp) {
        const expirationTime = tokenPayload.exp * 1000; // Convert to milliseconds
        localStorage.setItem('token_expiration', expirationTime.toString());
        console.log('Token stored, expires at:', new Date(expirationTime));
      } else {
        console.error('Failed to parse token or get expiration');
      }
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      // Check if demo expired (403 with demo_expired detail)
      if (error.response?.status === 403 && error.response?.data?.detail === 'demo_expired') {
        return {
          success: false,
          error: 'demo_expired'
        };
      }

      // Check if account is paused (403 with account_paused detail object)
      if (
        error.response?.status === 403 &&
        typeof error.response?.data?.detail === 'object' &&
        error.response.data.detail?.code === 'account_paused'
      ) {
        const det = error.response.data.detail;
        return {
          success: false,
          error: 'account_paused',
          pauseInfo: {
            organization_name: det.organization_name || 'klubben',
            reason: det.reason || 'Ingen årsak oppgitt',
            message: det.message || ''
          }
        };
      }

      // Handle validation errors (Pydantic returns array of error objects)
      let errorMessage = 'Innlogging mislyktes';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  // MULTI-CLUB: Complete login after user chooses an organization
  const selectOrganization = async (tempToken, organizationId) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/select-organization`, {
        temp_token: tempToken,
        organization_id: organizationId,
      });
      const { access_token, user: userData } = response.data;
      const tokenPayload = parseJwt(access_token);
      if (tokenPayload && tokenPayload.exp) {
        localStorage.setItem('token_expiration', (tokenPayload.exp * 1000).toString());
      }
      localStorage.setItem('token', access_token);
      // Remember last choice for "silent" multi-org login next time
      if (userData?.email) {
        localStorage.setItem(`preferred_org:${userData.email.toLowerCase()}`, organizationId);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Klubbvalg mislyktes',
      };
    }
  };

  // MULTI-CLUB: Switch active organization while already logged in
  const switchOrganization = async (organizationId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/api/auth/switch-organization`,
        { organization_id: organizationId },
      );
      const { access_token, user: userData } = response.data;
      const tokenPayload = parseJwt(access_token);
      if (tokenPayload && tokenPayload.exp) {
        localStorage.setItem('token_expiration', (tokenPayload.exp * 1000).toString());
      }
      localStorage.setItem('token', access_token);
      if (userData?.email) {
        localStorage.setItem(`preferred_org:${userData.email.toLowerCase()}`, organizationId);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Kunne ikke bytte klubb',
      };
    }
  };


  const register = async (formData) => {
    try {
      // Normalize email: lowercase and trim whitespace
      const normalizedFormData = {
        ...formData,
        email: formData.email.toLowerCase().trim()
      };
      
      await axios.post(`${API_BASE}/api/auth/register`, normalizedFormData);
      return { success: true, message: 'Registrering vellykket! Venter på godkjenning fra administrator.' };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registrering mislyktes' 
      };
    }
  };

  const initializeSystem = async (formData) => {
    try {
      await axios.post(`${API_BASE}/api/setup/init`, formData);
      setNeedsSetup(false);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Systeminitialisering mislyktes' 
      };
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('token_expiration');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setImpersonationContext(null);
  };

  const startImpersonation = async (organizationId) => {
    try {
      // Call backend to start impersonation
      const response = await axios.post(`${API_BASE}/api/auth/impersonate`, {
        organization_id: organizationId
      });
      
      if (response.data.access_token) {
        // Update token in localStorage and axios headers
        localStorage.setItem('token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        
        // Get updated user info with impersonation context
        const userResponse = await axios.get(`${API_BASE}/api/auth/me`);
        setUser(userResponse.data);
        
        // Set impersonation context
        if (userResponse.data.is_impersonating) {
          setImpersonationContext({
            orgId: userResponse.data.organization_id,
            orgName: userResponse.data.impersonating_org_name,
          });
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Start impersonation failed:', error);
      return false;
    }
  };

  const stopImpersonation = async () => {
    try {
      // Call backend to stop impersonation
      const response = await axios.post(`${API_BASE}/api/auth/stop-impersonation`);
      
      if (response.data.access_token) {
        // Update the auth token back to Super Admin
        localStorage.setItem('token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        
        // Get updated user info
        const userResponse = await axios.get(`${API_BASE}/api/auth/me`);
        setUser(userResponse.data);
        setImpersonationContext(null);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      return false;
    }
  };

  // Refresh user data from server (useful after organization settings change)
  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const userResponse = await axios.get(`${API_BASE}/api/auth/me`);
        setUser(userResponse.data);
        console.log('User data refreshed:', userResponse.data.email);
        return true;
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return false;
    }
  };

  const value = {
    user,
    updateUser,
    refreshUser,
    loading,
    needsSetup,
    login,
    selectOrganization,
    switchOrganization,
    register,
    logout,
    initializeSystem,
    startImpersonation,
    stopImpersonation,
    impersonationContext,
    isAuthenticated: !!user,
    isSuperAdmin: user?.original_role === 'super_admin' || user?.role === 'super_admin',
    isAdmin: user?.role === 'admin',
    isOrgAdmin: user?.role === 'org_admin',
    isRangeOfficer: user?.role === 'range_officer',
    isMember: user?.role === 'member',
    isImpersonating: user?.is_impersonating || !!impersonationContext
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};